
import { Component, Vue } from 'vue-property-decorator'
import {
  getAllContracts,
  getContractStatistics,
  createContractVersion,
  updateContract,
  toggleContractStatus,
  deleteContract,
  IContract,
  IContractStatistics,
  ICreateContractRequest,
  IUpdateContractRequest
} from '@/api/contracts'
import { Form } from 'element-ui'

const defaultContractData: IContract = {
  contractName: '',
  version: '',
  isActive: true,
  localizationKey: '',
  description: ''
}

@Component({
  name: 'ContractManagement',
  filters: {
    formatDate(value: string) {
      if (!value) return '-'
      const date = new Date(value)
      const day = date.getDate().toString().padStart(2, '0')
      const month = (date.getMonth() + 1).toString().padStart(2, '0')
      const year = date.getFullYear()
      const hours = date.getHours().toString().padStart(2, '0')
      const minutes = date.getMinutes().toString().padStart(2, '0')
      return `${day}.${month}.${year} ${hours}:${minutes}`
    }
  }
})
export default class extends Vue {
  private list: IContract[] = []
  private listLoading = true
  private listQuery = {
    contractName: '',
    isActive: null as boolean | null
  }

  private statistics: IContractStatistics = {
    totalContracts: 0,
    activeContracts: 0,
    totalAcceptances: 0,
    acceptanceBreakdown: []
  }

  private tempContractData = Object.assign({}, defaultContractData, {
    deactivateOldVersions: true
  })

  private selectedContract: IContract = Object.assign({}, defaultContractData)
  private dialogFormVisible = false
  private dialogDetailsVisible = false
  private dialogStatus = ''

  private rules = {
    contractName: [{ required: true, message: 'Contract name is required', trigger: 'blur' }],
    version: [{ required: true, message: 'Version is required', trigger: 'blur' }],
    localizationKey: [{ required: true, message: 'Localization key is required', trigger: 'blur' }]
  }

  get uniqueContractNames() {
    const names = new Set(this.list.map(c => c.contractName))
    return names.size
  }

  created() {
    this.getList()
    this.getStatistics()
  }

  private async getList() {
    this.listLoading = true
    try {
      const params: any = {}
      if (this.listQuery.contractName) params.contractName = this.listQuery.contractName
      if (this.listQuery.isActive !== null) params.isActive = this.listQuery.isActive

      const { data } = await getAllContracts(params)
      this.list = data.data || []
    } catch (error) {
      console.error('Failed to get contracts:', error)
      this.$message.error('Failed to load contracts')
    }
    this.listLoading = false
  }

  private async getStatistics() {
    try {
      const { data } = await getContractStatistics()
      this.statistics = data.data
    } catch (error) {
      console.error('Failed to get statistics:', error)
    }
  }

  private handleFilter() {
    this.getList()
  }

  private resetTempContractData() {
    this.tempContractData = Object.assign({}, defaultContractData, {
      deactivateOldVersions: true
    })
  }

  private handleCreate() {
    this.resetTempContractData()
    this.dialogStatus = 'create'
    this.dialogFormVisible = true
    this.$nextTick(() => {
      (this.$refs.dataForm as Form).clearValidate()
    })
  }

  private createData() {
    (this.$refs.dataForm as Form).validate(async(valid) => {
      if (valid) {
        try {
          const createData: ICreateContractRequest = {
            contractName: this.tempContractData.contractName,
            version: this.tempContractData.version,
            localizationKey: this.tempContractData.localizationKey,
            description: this.tempContractData.description,
            deactivateOldVersions: this.tempContractData.deactivateOldVersions
          }

          await createContractVersion(createData)
          this.$message.success('Contract version created successfully')
          this.dialogFormVisible = false
          this.getList()
          this.getStatistics()
        } catch (error: any) {
          console.error('Failed to create contract:', error)
          const message = error?.response?.data?.status?.message || 'Failed to create contract'
          this.$message.error(message)
        }
      }
    })
  }

  private handleUpdate(row: IContract) {
    this.tempContractData = Object.assign({}, row, { deactivateOldVersions: true })
    this.dialogStatus = 'update'
    this.dialogFormVisible = true
    this.$nextTick(() => {
      (this.$refs.dataForm as Form).clearValidate()
    })
  }

  private updateData() {
    (this.$refs.dataForm as Form).validate(async(valid) => {
      if (valid) {
        try {
          const updateData: IUpdateContractRequest = {
            localizationKey: this.tempContractData.localizationKey,
            description: this.tempContractData.description
          }

          await updateContract(this.tempContractData.id!, updateData)
          this.$message.success('Contract updated successfully')
          this.dialogFormVisible = false
          this.getList()
        } catch (error) {
          console.error('Failed to update contract:', error)
          this.$message.error('Failed to update contract')
        }
      }
    })
  }

  private handleViewDetails(row: IContract) {
    this.selectedContract = Object.assign({}, row)
    this.dialogDetailsVisible = true
  }

  private handleToggle(row: IContract) {
    this.$confirm(
      `Are you sure you want to ${row.isActive ? 'deactivate' : 'activate'} this contract?`,
      'Confirm',
      {
        confirmButtonText: 'Yes',
        cancelButtonText: 'Cancel',
        type: 'warning'
      }
    ).then(async() => {
      try {
        await toggleContractStatus(row.id!, { isActive: !row.isActive })
        this.$message.success(`Contract ${row.isActive ? 'deactivated' : 'activated'} successfully`)
        this.getList()
        this.getStatistics()
      } catch (error) {
        console.error('Failed to toggle contract status:', error)
        this.$message.error('Failed to toggle contract status')
      }
    }).catch(() => {
      this.$message.info('Operation cancelled')
    })
  }

  private handleDelete(row: IContract) {
    if (row.acceptanceCount && row.acceptanceCount > 0) {
      this.$message.warning('Cannot delete contract with user acceptances (GDPR compliance)')
      return
    }

    this.$confirm(
      'This will permanently delete this contract version. Continue?',
      'Warning',
      {
        confirmButtonText: 'Delete',
        cancelButtonText: 'Cancel',
        type: 'error'
      }
    ).then(async() => {
      try {
        await deleteContract(row.id!)
        this.$message.success('Contract deleted successfully')
        this.getList()
        this.getStatistics()
      } catch (error: any) {
        console.error('Failed to delete contract:', error)
        const message = error?.response?.data?.data?.message || 'Failed to delete contract'
        this.$message.error(message)
      }
    }).catch(() => {
      this.$message.info('Operation cancelled')
    })
  }
}
